import { Editor } from '@tinymce/tinymce-react';
import { toast } from "sonner";
import { apiFormData } from "../services/api";

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  height?: number;
  placeholder?: string;
  folder?: string;
}

export function RichTextEditor({ 
  value, 
  onChange, 
  height = 600, 
  placeholder = "Comece a escrever...",
  folder = "geral"
}: RichTextEditorProps) {
  return (
    <div className="mt-2 bg-white rounded-lg border border-gray-300 overflow-hidden">
      <Editor
        apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
        value={value}
        onEditorChange={onChange}
        init={{
          height: height,
          width: '100%',
          menubar: false,
          placeholder: placeholder,
          language: 'pt_BR',
          language_url: 'https://cdn.jsdelivr.net/npm/tinymce-i18n@latest/langs/pt_BR.min.js',
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
          ],
          promotion: false,
          branding: false,
          setup: (editor: any) => {
            // Botão de Menu Unificado para Inserir Blocos
            editor.ui.registry.addMenuButton('insert_blocks', {
              text: 'Inserir Bloco',
              tooltip: 'Modelos de conteúdo prontos',
              fetch: (callback: any) => {
                const items = [
                  {
                    type: 'menuitem',
                    text: '📦 Estrutura da Página (Container)',
                    onAction: () => editor.insertContent('<div class="max-w-4xl mx-auto px-4 py-16"><div class="bg-white"><div class="px-4"><div class="space-y-12 gap-4 flex flex-col"><p>Conteúdo aqui...</p></div></div></div></div>')
                  },
                  {
                    type: 'menuitem',
                    text: '🚀 CTA (Chamada para Ação)',
                    onAction: () => editor.insertContent('<div class="mt-8 text-center p-8 bg-gray-50 rounded-lg"><h3 class="text-2xl font-semibold mb-4" style="color: #68686A;">Título do CTA</h3><p class="text-gray-600 mb-6 max-w-2xl mx-auto">Descrição do CTA aqui.</p><div class="flex gap-4 justify-center"><a href="/" target="_blank" style="text-decoration: none;"><button class="px-8 py-3 text-white rounded" style="background-color: #849D70; border: none; cursor: pointer;">Botão Principal</button></a><a href="#" style="text-decoration: none;"><button class="px-8 py-3 border border-gray-300 rounded" style="background: transparent; color: #68686A; cursor: pointer;">Botão Secundário</button></a></div></div><p>&nbsp;</p>')
                  },
                  {
                    type: 'menuitem',
                    text: '📌 Passo a Passo',
                    onAction: () => editor.insertContent('<div class="flex gap-8 items-start mb-8"><div class="step-circle" style="background-color: #849D70; width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 20px; flex-shrink: 0;">1</div><div class="flex-1"><h3 class="text-2xl font-semibold mb-3" style="color: #68686A;">Título da Etapa</h3><div class="text-gray-600 text-lg">Descrição da etapa.</div></div></div><p>&nbsp;</p>')
                  },
                  {
                    type: 'menuitem',
                    text: '💙 Bloco de Doação',
                    onAction: () => editor.insertContent('<div class="bg-gray-50 p-8 rounded-lg my-8"><h3 class="text-2xl font-semibold mb-6" style="color: #68686A;">Como Doar</h3><div class="grid grid-cols-1 md:grid-cols-2 gap-6"><div class="bg-white p-6 border border-gray-200 rounded"><h4 class="font-semibold mb-3" style="color: #849D70;">Doe com PIX</h4><p class="text-gray-600">chave@exemplo.org</p></div><div class="bg-white p-6 border border-gray-200 rounded"><h4 class="font-semibold mb-3" style="color: #849D70;">Site Oficial</h4><a href="#" class="text-gray-600 underline">linkaqui.org/doe</a></div></div></div><p>&nbsp;</p>')
                  },
                  {
                    type: 'menuitem',
                    text: '📷 Legenda de Foto',
                    onAction: () => editor.insertContent('<div class="bg-gray-50 p-1 m-0 w-100">Legenda para Foto</div><p>&nbsp;</p>')
                  },
                  {
                    type: 'menuitem',
                    text: '📌 Texto com Fundo',
                    onAction: () => editor.insertContent('<div class="py-12 px-6 my-8 text-center" style="background-color: #F9FAFB; border-radius: 8px; display:block; clear:both;"><h3 class="text-2xl font-semibold mb-4" style="color: #68686A;">Título da Seção</h3><p class="text-gray-600 max-w-2xl mx-auto">Descrição adicional.</p></div><p>&nbsp;</p>')
                  },
                  {
                    type: 'menuitem',
                    text: '💬 Citação Destacada',
                    onAction: () => editor.insertContent('<p class="italic mt-8 p-4 border-l-4" style="border-color: #D4C070; background-color: #f9fafb;">Texto da citação aqui<br><span class="block mt-2 font-semibold">- Nome Autor</span></p><p>&nbsp;</p>')
                  },
                  {
                    type: 'menuitem',
                    text: '📰 Artigo com Leia Mais',
                    onAction: () => editor.insertContent(`<div class="artigo-container">
  <div class="artigo-header-text">
    <h2 class="text-3xl font-bold mb-4 text-center" style="color: #68686A;">Título da Matéria</h2>
  </div>

  <div class="artigo-conteudo-corpo">
    <div class="prose-lg">
      <p class="text-gray-600 mb-4 font-bold" style="font-weight:700; color:#4b5563;">Subtítulo do artigo</p>
      
      <div class="artigo-texto-wrapper">
        <p class="text-gray-600 mb-4" style="color:#4b5563;">Primeiro parágrafo do artigo. Este texto aparece inicialmente antes do blur.</p>
        
        <div class="artigo-continuacao">
           <p class="text-gray-600 mb-4" style="color:#4b5563;">Este texto ficará escondido atrás do efeito de "blur" e aparecerará ao clicar em Leia Mais.</p>
           <p class="text-gray-600" style="color:#4b5563;">Continue escrevendo o restante do seu artigo aqui dentro desta área.</p>
        </div>
      </div>
      
      <button class="btn-leia-mais">Leia Mais</button>
    </div>
  </div>
</div>
<p>&nbsp;</p>`)
                  }
                ];
                callback(items);
              }
            });
          },
          toolbar: 'insert_blocks | undo redo | styles blocks | ' +
            'bold italic forecolor | alignleft aligncenter ' +
            'alignright alignjustify | bullist numlist outdent indent | ' +
            'image link | code removeformat',
          content_css: false,
          content_style: `
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');

            html { font-size: 14px; }
            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              font-size: 14px;
              color: #374151;
              line-height: 1.6;
              padding: 2.5rem;
              max-width: 850px;
              margin: 0 auto;
              background-color: #fff;
            }
            * { box-sizing: border-box; }

            h1, h2, h3, h4, h5, h6 { color: #68686A; margin-top: 2rem; margin-bottom: 1rem; line-height: 1.2; }
            h1 { font-size: 2rem; font-weight: 700; }
            h2 { font-size: 1.5rem; font-weight: 700; }
            h3 { font-size: 1.25rem; font-weight: 600; }

            p { margin-bottom: 1.5rem; }
            img { max-width: 100%; height: auto; border-radius: 4px; display: block; margin: 1.5rem auto; }

            /* Estilos específicos para o Bloco de "Leia Mais" (Legado e Novo) */
            .artigo-container {
              width: 100%;
              margin-bottom: 4rem;
              clear: both;
              border-bottom: 1px solid #eee;
              padding-bottom: 4rem;
            }
            .artigo-header-text { width: 100%; text-align: center; margin-bottom: 2rem; }
            .artigo-conteudo-corpo { width: 100%; }
            
            .artigo-texto-wrapper {
              position: relative;
              max-height: 400px;
              overflow: hidden;
              transition: max-height 1s ease;
              border: 1px dashed #D4C070;
              padding: 1.5rem;
              border-radius: 8px;
              background: rgba(212, 192, 112, 0.05);
            }
            
            /* Efeito de Blur Legado/Novo para o editor visualizar o corte */
            .artigo-texto-wrapper::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 0;
              width: 100%;
              height: 120px;
              background: linear-gradient(transparent, rgba(255,255,255,0.9));
              pointer-events: none;
            }

            .artigo-continuacao {
              margin-top: 1.5rem;
              padding-top: 1.5rem;
              border-top: 1px dashed #D4C070;
              background: rgba(212, 192, 112, 0.08);
            }

            .btn-leia-mais {
              display: block;
              margin: 2rem auto;
              padding: 0.8rem 2.5rem;
              background-color: #D4C070;
              color: white;
              border: none;
              border-radius: 4px;
              font-weight: 700;
              font-size: 0.9rem;
              cursor: pointer;
              text-align: center;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }

            /* Estilos de Utilidade */
            .text-center { text-align: center; }
            .prose-lg { font-size: 1.15rem; }
            .font-bold { font-weight: 700; }
            .italic { font-style: italic; }
            .text-gray-600 { color: #4b5563; }
            .bg-gray-50 { background-color: #f9fafb; }
            .rounded-lg { border-radius: 0.5rem; }
            .p-8 { padding: 2rem; }
            .my-8 { margin-top: 2rem; margin-bottom: 2rem; }
            .mt-8 { margin-top: 2rem; }
            .mb-4 { margin-bottom: 1rem; }
            .mb-6 { margin-bottom: 1.5rem; }
            .flex { display: flex; }
            .gap-4 { gap: 1rem; }
            .gap-6 { gap: 1.5rem; }
            .justify-center { justify-content: center; }
            .max-w-4xl { max-width: 56rem; }
            .max-w-2xl { max-width: 42rem; }
            .mx-auto { margin-left: auto; margin-right: auto; }
            .grid { display: grid; }
            .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
            .md\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          `,
          style_formats: [
            {
              title: 'Listas e Etapas', items: [
                { title: 'Passo a Passo: Número ao Lado', block: 'ol', classes: 'etapas-laterais' },
                { title: 'Passo a Passo: Número no Topo', block: 'ol', classes: 'etapas-acima' },
                { title: 'Lista: Bullets Verdes', block: 'ul', classes: 'bullets-verdes' }
              ]
            }
          ],
          images_upload_handler: async (blobInfo: any, progress: any) => {
            return new Promise((resolve, reject) => {
              const formDataImg = new FormData();
              formDataImg.append('file', blobInfo.blob(), blobInfo.filename());
              formDataImg.append('folder', folder);

              apiFormData.post('/api/admin/upload/send', formDataImg, {
                onUploadProgress: (e: any) => {
                  if (e.total && progress) progress((e.loaded / e.total) * 100);
                }
              })
                .then((res: any) => {
                  const imageUrl = res.data.location;
                  resolve(imageUrl);
                  toast.success('Imagem enviada com sucesso!');
                })
                .catch((err: any) => {
                  console.error('Upload error:', err);
                  reject('Falha ao enviar imagem para o servidor.');
                  toast.error('Erro ao enviar imagem');
                });
            });
          },
          image_advtab: false,
          image_title: true,
          image_caption: false,
          image_class: 'img-responsive',
          image_prepend_url: '',
          automatic_uploads: true,
        }}
      />

      <style>{`
        .tox-tinymce-aux {
          z-index: 10000 !important;
        }
        .tox-opened {
          z-index: 10001 !important;
        }
      `}</style>
    </div>
  );
}
